import {ChangeDetectionStrategy, Component, computed, inject, OnDestroy} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {DatePipe} from '@angular/common';
import {CallStatisticSummary} from '../../../../../api';
import {
  PeriodNavigatorComponent,
  PeriodRange,
  PeriodType
} from '../../../../components/period-navigator/period-navigator';
import {Loader} from '../../../../components/loader/loader';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {CallStatisticStoreService} from '../../services/call-statistic-store.service';
import {CallStatisticGrid} from '../call-statistic-grid/call-statistic-grid';
import {CallStatisticCard} from '../call-statistic-card/call-statistic-card';
import {CallDurationPipe, formatCallDuration} from '../../pipes/call-duration.pipe';

const DASH = '—';

interface TotalCard {
  label: string;
  value: string;
}

@Component({
  selector: 'elcia-call-statistic-home',
  imports: [
    TranslatePipe,
    DatePipe,
    PeriodNavigatorComponent,
    Loader,
    ElciaButton,
    ElciaDialog,
    CallStatisticGrid,
    CallStatisticCard,
    CallDurationPipe,
  ],
  templateUrl: './call-statistic-home.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallStatisticHome implements OnDestroy {
  private readonly store = inject(CallStatisticStoreService);

  protected readonly PeriodType = PeriodType;
  protected readonly callStatistics = this.store.callStatistics;
  protected readonly isLoading = this.store.isLoading;
  protected readonly isLiveDisplayed = this.store.isLiveRequested;
  protected readonly liveStatistics = this.store.liveStatistics;
  protected readonly isLoadingLive = this.store.isLoadingLive;
  protected readonly isLiveError = this.store.isLiveError;

  // Totals of the displayed rows, so the cards always add up to what the grid shows.
  protected readonly cards = computed<TotalCard[]>(() => {
    const statistics = this.callStatistics();
    const sum = (select: (statistic: CallStatisticSummary) => number) =>
      statistics.reduce((total, statistic) => total + select(statistic), 0);

    const answered = sum(s => s.callsAnswered);
    const lost = sum(s => s.callsLost);
    const callDuration = sum(s => s.totalCallDurationSeconds);

    // Answered over the calls that reached an outcome, and talk time spread over the answered ones
    // only: a lost call adds no conversation. A dash rather than a misleading 0 when nothing happened.
    const handled = answered + lost;
    const answerRate = handled === 0 ? DASH : `${Math.round((answered / handled) * 100)} %`;
    const averageCallDuration = answered === 0 ? DASH : formatCallDuration(Math.round(callDuration / answered));

    return [
      {label: 'call-statistic.calls-total', value: `${sum(s => s.callsTotal)}`},
      {label: 'call-statistic.calls-answered', value: `${answered}`},
      {label: 'call-statistic.calls-lost', value: `${lost}`},
      {label: 'call-statistic.answer-rate', value: answerRate},
      {label: 'call-statistic.total-call-duration', value: formatCallDuration(callDuration)},
      {label: 'call-statistic.average-call-duration', value: averageCallDuration},
      {
        label: 'call-statistic.total-waiting-duration',
        value: formatCallDuration(sum(s => s.totalWaitingDurationSeconds))
      },
    ];
  });

  protected onPeriodChange(range: PeriodRange): void {
    this.store.filterParams.set({startDate: range.startDate, endDate: range.endDate});
  }

  protected showLive(): void {
    this.store.isLiveRequested.set(true);
  }

  protected closeLive(): void {
    this.store.isLiveRequested.set(false);
  }

  // Leaving the screen with the dialog open would keep the live query enabled application wide.
  ngOnDestroy(): void {
    this.store.isLiveRequested.set(false);
  }
}
